import { useEffect } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import type { RichEditorProps } from '@docgen/sdk';
import { Bold, Italic, Link2, List, ListOrdered } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * The rich-text engine inside RichSlot: TipTap (ProseMirror) with a bubble
 * toolbar on selection. Marks are limited to what the documents actually
 * use — bold, italic, links, lists — so pasted content stays on-brand.
 * Commits on blur, which feeds the same slot-edit → bind-write-through path
 * as every other edit.
 */
export function RichTextEditor({ value, onCommit }: RichEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Link.configure({ openOnClick: false }),
    ],
    content: value,
    onBlur: ({ editor: e }) => onCommit(e.getHTML()),
  });

  // external updates (undo/redo, tool-pane or data edits) sync in when idle
  useEffect(() => {
    if (editor && !editor.isFocused && editor.getHTML() !== value) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return null;

  const buttonClass = (active: boolean) =>
    cn(
      'flex size-6 items-center justify-center rounded text-foreground/80 hover:bg-accent hover:text-foreground',
      active && 'bg-primary/15 text-primary',
    );

  const setLink = () => {
    const existing = editor.getAttributes('link')['href'] as string | undefined;
    const url = window.prompt('Link URL', existing ?? 'https://');
    if (url === null) return;
    if (url === '') editor.chain().focus().unsetLink().run();
    else editor.chain().focus().setLink({ href: url }).run();
  };

  return (
    <>
      <BubbleMenu editor={editor}>
        <div className="flex items-center gap-0.5 rounded-lg border bg-popover p-0.5 shadow-lg">
          <button type="button" aria-label="Bold" className={buttonClass(editor.isActive('bold'))}
            onClick={() => editor.chain().focus().toggleBold().run()}>
            <Bold className="size-3.5" />
          </button>
          <button type="button" aria-label="Italic" className={buttonClass(editor.isActive('italic'))}
            onClick={() => editor.chain().focus().toggleItalic().run()}>
            <Italic className="size-3.5" />
          </button>
          <button type="button" aria-label="Link" className={buttonClass(editor.isActive('link'))}
            onClick={setLink}>
            <Link2 className="size-3.5" />
          </button>
          <button type="button" aria-label="Bullet list" className={buttonClass(editor.isActive('bulletList'))}
            onClick={() => editor.chain().focus().toggleBulletList().run()}>
            <List className="size-3.5" />
          </button>
          <button type="button" aria-label="Numbered list" className={buttonClass(editor.isActive('orderedList'))}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}>
            <ListOrdered className="size-3.5" />
          </button>
        </div>
      </BubbleMenu>
      <EditorContent editor={editor} />
    </>
  );
}
